import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  message,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import { DownloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import useDebounce from "../../hooks/useDebounce";
import { numFormatter } from "../../Utils/formatter";
import {
  getCommissionTaxReport,
  getMangoCreditsTaxReport,
  getWhatsappSubscriptionTaxReport,
  getTaxReportForCreator,
} from "../api";

const { Option } = Select;

const TaxReports = () => {
  const [search, setSearch] = useState("");
  const [dateString, setDateString] = useState(moment().format("YYYY-MM"));
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reportType, setReportType] = useState("commissions");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalFieldsData, setTotalFieldsData] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());
  const [creatorCountryType, setCreatorCountryType] = useState(null);
  const [taxReportLoading, setTaxReportLoading] = useState({});

  const gridStyle = {
    width: "50%",
    textAlign: "center",
    height: "100%",
  };

  const topGridStyle = {
    width: "100%",
    textAlign: "center",
    height: "100%",
  };

  const cardStyle = {
    height: "100%",
    textAlign: "center",
  };

  const handleGetTaxReport = async (creatorId) => {
    setTaxReportLoading((prev) => ({ ...prev, [creatorId]: true }));
    try {
      const month = moment(dateString, "YYYY-MM").format("MM-YYYY");
      const resp = await getTaxReportForCreator(month, creatorId);
      fileDownload(resp, `tax-report-${creatorId}-${month}.pdf`);
    } catch (err) {
      message.error("Something went wrong while downloading tax report");
    } finally {
      setTaxReportLoading((prev) => ({ ...prev, [creatorId]: false }));
    }
  };

  const getCardsToDisplayOnReportType = () => {
    switch (reportType) {
      case "commissions": {
        return (
          <>
            <Col span={6}>
              <Card title="Earnings" bordered={false} style={cardStyle}>
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalEarning?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Commission:</strong> <br />
                  {totalFieldsData?.totalCommission?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>Convenience:</strong> <br />
                  {totalFieldsData?.totalConvenience?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                title="Commission Charged"
                bordered={false}
                style={cardStyle}
              >
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalCommission?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Without GST:</strong> <br />
                  {totalFieldsData?.totalCommissionWithoutGst?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>GST:</strong> <br />
                  {totalFieldsData?.totalCommissionGst?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                title="Convenience Charged"
                bordered={false}
                style={cardStyle}
              >
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalConvenience?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Without GST:</strong> <br />
                  {totalFieldsData?.totalConvenienceWithoutGst?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>GST:</strong> <br />
                  {totalFieldsData?.totalConvenienceGst?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={6}>
              <Card title="Total Creators" bordered={false} style={cardStyle}>
                <Typography>{total}</Typography>
              </Card>
            </Col>
          </>
        );
      }
      case "whatsappsubscriptions": {
        return (
          <>
            <Col span={12}>
              <Card
                title="Total Amount Charged"
                bordered={false}
                style={cardStyle}
              >
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalPaid?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Without GST:</strong> <br />
                  {totalFieldsData?.totalPaidWithoutGst?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>GST:</strong> <br />
                  {totalFieldsData?.totalPaidGst?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Total Creators" bordered={false} style={cardStyle}>
                <Typography>{total}</Typography>
              </Card>
            </Col>
          </>
        );
      }
      case "mangocredits": {
        return (
          <>
            <Col span={8}>
              <Card
                title="Total Amount Charged"
                bordered={false}
                style={cardStyle}
              >
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalAmount?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Without GST:</strong> <br />
                  {totalFieldsData?.totalAmountWithoutGst?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>GST:</strong> <br />
                  {totalFieldsData?.totalGst?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={8}>
              <Card
                title="Total Convenience Charged"
                bordered={false}
                style={cardStyle}
              >
                <Card.Grid style={topGridStyle}>
                  {totalFieldsData?.totalConvenience?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle} title="Commission">
                  <strong>Without GST:</strong> <br />
                  {totalFieldsData?.totalConvenienceWithoutGst?.toFixed(2)}
                </Card.Grid>
                <Card.Grid style={gridStyle}>
                  <strong>GST:</strong> <br />
                  {totalFieldsData?.totalConvenienceGst?.toFixed(2)}
                </Card.Grid>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Total Creators" bordered={false} style={cardStyle}>
                <Typography>{total}</Typography>
              </Card>
            </Col>
          </>
        );
      }
      default:
        return null;
    }
  };

  const getTableOnReportType = () => {
    const detailsColumns = [
      {
        title: "Creator",
        dataIndex: "creatorId",
        key: "creatorId",
        render: (text, record) => (
          <TableUserInfoItem
            name={record?.creatorName}
            avatar={record?.creatorProfilePicUrl}
            hideContact
          />
        ),
      },
    ];
    const isCurrentMonth = moment().format("YYYY-MM") === dateString;
    const actionsColumn = isCurrentMonth
      ? []
      : [
          {
            title: "Action",
            key: "action",
            render: (text, record) => (
              <Button
                onClick={() => handleGetTaxReport(record.creatorId)}
                loading={taxReportLoading[record.creatorId]}
                type="primary"
              >
                Get Tax Report
              </Button>
            ),
          },
        ];
    const gstColumns = [
      {
        title: "GST Number",
        dataIndex: "gstNumber",
        key: "gstNumber",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            <Typography>{record?.gstNumber}</Typography>
            <Typography>{record?.gstName}</Typography>
          </Space>
        ),
      },
      {
        title: "GST Address",
        dataIndex: "gstRegistrationAddress",
        key: "gstRegistrationAddress",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            <Typography>Address: {record?.gstRegistrationAddress}</Typography>
            <Typography>
              Pincode: {record?.gstRegistrationAddressPincode}
            </Typography>
            <Typography>State: {record?.gstState}</Typography>
          </Space>
        ),
      },
    ];

    let specificColumns = [];

    switch (reportType) {
      case "commissions": {
        specificColumns = [
          {
            title: "Commission",
            dataIndex: "totalCommissionWithoutGst",
            key: "totalCommissionWithoutGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "Commission GST",
            dataIndex: "totalCommissionGst",
            key: "totalCommissionGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "Convenience",
            dataIndex: "totalConvenienceWithoutGst",
            key: "totalConvenienceWithoutGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "Convenience GST",
            dataIndex: "totalConvenienceGst",
            key: "totalConvenienceGst",
            render: (amount) => numFormatter({ amount }),
          },
        ];
        break;
      }
      case "whatsappsubscriptions": {
        specificColumns = [
          {
            title: "Amount (Without GST)",
            dataIndex: "totalPaidWithoutGst",
            key: "totalPaidWithoutGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "GST",
            dataIndex: "totalPaidGst",
            key: "totalPaidGst",
            render: (amount) => numFormatter({ amount }),
          },
        ];
        break;
      }
      case "mangocredits": {
        specificColumns = [
          {
            title: "Amount (Without GST)",
            dataIndex: "totalAmountWithoutGst",
            key: "totalAmountWithoutGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "GST",
            dataIndex: "totalGst",
            key: "totalGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "Convenience (Without GST)",
            dataIndex: "totalConvenienceWithoutGst",
            key: "totalConvenienceWithoutGst",
            render: (amount) => numFormatter({ amount }),
          },
          {
            title: "Convenience GST",
            dataIndex: "totalConvenienceGst",
            key: "totalConvenienceGst",
            render: (amount) => numFormatter({ amount }),
          },
        ];
        break;
      }
      default:
        break;
    }
    return (
      <Table
        key={tableKey}
        columns={
          loading
            ? []
            : [
                ...detailsColumns,
                ...specificColumns,
                ...gstColumns,
                ...actionsColumn,
              ]
        }
        dataSource={data}
        pagination={{
          total,
          current: page,
          onChange: setPage,
          showSizeChanger: false,
          pageSize: 10,
        }}
        loading={loading}
        locale={{
          emptyText: loading ? "Loading..." : "No tax reports found",
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  const getDataOnReportType = async () => {
    setLoading(true);
    let resp;
    let totalFields = [];
    const mom = moment(`${dateString}-02T18:30Z`);
    const startDate = mom.startOf("month")?.toISOString();
    const endDate = mom?.endOf("month")?.toISOString();
    try {
      switch (reportType) {
        case "commissions": {
          resp = await getCommissionTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            "inr",
            page,
            10
          );
          totalFields = [
            "totalEarning",
            "totalCommission",
            "totalConvenience",
            "totalCommissionWithoutGst",
            "totalCommissionGst",
            "totalConvenienceWithoutGst",
            "totalConvenienceGst",
          ];
          break;
        }
        case "whatsappsubscriptions": {
          resp = await getWhatsappSubscriptionTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            page,
            10
          );
          totalFields = ["totalPaid", "totalPaidWithoutGst", "totalPaidGst"];
          break;
        }
        case "mangocredits": {
          resp = await getMangoCreditsTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            page,
            10
          );
          totalFields = [
            "totalAmount",
            "totalAmountWithoutGst",
            "totalGst",
            "totalConvenience",
            "totalConvenienceWithoutGst",
            "totalConvenienceGst",
          ];
          break;
        }
        default:
          break;
      }
      if (resp?.code === 0) {
        setData(resp?.result?.data);
        setTotal(resp?.result?.total);
        const tempTotalFieldsData = {};
        totalFields.forEach((field) => {
          tempTotalFieldsData[field] = resp?.result?.[field];
        });
        setTotalFieldsData(tempTotalFieldsData);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const getCSVOnReportType = async (currency = "inr") => {
    setCsvLoading(true);
    let resp;
    const mom = moment(`${dateString}-02T18:30Z`);
    const startDate = mom.startOf("month")?.toISOString();
    const endDate = mom?.endOf("month")?.toISOString();
    try {
      switch (reportType) {
        case "commissions": {
          resp = await getCommissionTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            currency,
            undefined,
            undefined,
            true
          );
          break;
        }
        case "whatsappsubscriptions": {
          resp = await getWhatsappSubscriptionTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            undefined,
            undefined,
            true
          );
          break;
        }
        case "mangocredits": {
          resp = await getMangoCreditsTaxReport(
            startDate,
            endDate,
            debouncedSearch,
            creatorCountryType,
            undefined,
            undefined,
            true
          );
          break;
        }
        default:
          break;
      }
      fileDownload(resp, `${reportType}-${Date.now()}.csv`);
    } catch (err) {
      message.error("Something went wrong");
    } finally {
      setCsvLoading(false);
    }
  };

  useEffect(() => {
    getDataOnReportType();
  }, [page, dateString, debouncedSearch, reportType, creatorCountryType]);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: "16px",
          width: "100%",
        }}
      >
        {getCardsToDisplayOnReportType()}
      </Row>

      <FiltersWrapper>
        <CustomSearchbar
          placeholder="Search by name"
          searchQuery={search}
          setSearchQuery={(value) => {
            setSearch(value);
            setPage(1);
          }}
          style={{ maxWidth: 500 }}
        />

        <DatePicker
          size="large"
          picker="month"
          onChange={(_, ds) => {
            setDateString(ds);
          }}
          value={dateString ? moment(dateString, "YYYY-MM") : null}
          style={{ marginLeft: "auto" }}
        />

        <Select
          size="large"
          placeholder="Select approval status"
          onSelect={setReportType}
          value={reportType}
        >
          <Option value="commissions">Commissions</Option>
          <Option value="whatsappsubscriptions">Whatsapp Subscriptions</Option>
          <Option value="mangocredits">Mango Credits</Option>
        </Select>

        <Select
          size="large"
          allowClear
          placeholder="Select country type"
          value={creatorCountryType}
          onSelect={(value) => setCreatorCountryType(value)}
          onClear={() => setCreatorCountryType(null)}
        >
          <Option value="india">India</Option>
          <Option value="international">International</Option>
        </Select>

        <Button
          className="siteBtn grayBtn"
          onClick={() => getCSVOnReportType()}
          loading={csvLoading}
        >
          <DownloadOutlined />
          Export CSV
        </Button>
        <Button
          className="siteBtn grayBtn"
          onClick={() => getCSVOnReportType("usd")}
          loading={csvLoading}
        >
          <DownloadOutlined />
          Export CSV in USD
        </Button>
      </FiltersWrapper>

      {getTableOnReportType()}
    </>
  );
};

export default TaxReports;
